// Dealer map basemap fix (Nyan Gear). Loaded defer AFTER leaflet.js, BEFORE dealer-map.js.
//
// 1. BASEMAP: monkeypatches L.tileLayer so dealer-map.js's CARTO basemap becomes Esri's
//    KEYLESS World Imagery (colored satellite, no watermark, no API key). Esri raster
//    tiles are {z}/{y}/{x} (y before x).
// 2. CRISP TILES (no seams/grid, no blur): the theme creates the map with zoomSnap:0,
//    i.e. fractional zoom, which scales raster tiles off the pixel grid and produces
//    seams + blur. We force INTEGER zoom (zoomSnap/zoomDelta = 1) instead. Never touch
//    tileSize (a 257px hack made a hard grid).
// 3. PINCH STILL WORKS: laptop trackpad pinch arrives as ctrl+wheel with tiny fractional
//    deltas; with zoomSnap:1 alone Leaflet snaps those away and pinch dies. dealer-map.js
//    also registers its own fractional ctrl+wheel handler. So we register OUR integer-step
//    ctrl+wheel handler first (and in the capture phase — the wheel target is always a
//    descendant pane, so capture fires before dealer-map.js's bubble handler) and call
//    stopImmediatePropagation() so the fractional handler never runs.
// 4. STATE LINES ONLY + EXACTLY ONE SET OF LABELS: boundaries are drawn as crisp VECTOR
//    lines from our own bundled GeoJSON theme asset (assets/us-states-outline.json —
//    same origin, keyless, no external service; URL derived from this script's own src
//    so it survives theme copies/publishes). The dataset contains STATE polygons only,
//    so no county subdivisions can ever appear at any zoom. Labels are our own single
//    12px set (z3–6). Fallback if the asset ever fails: a CORS-enabled public copy of
//    the same state-only GeoJSON; if that also fails, we show labels with no lines.
//    We deliberately do NOT fall back to Esri's raster Boundaries & Places overlay:
//    it bakes in its own labels (duplicates) AND county lines at higher zooms.
//    (Esri's USA_States_Generalized FeatureServer now returns "Token Required", so the
//    old vector-service approach is gone for good.)
// 5. ZOOM-INVARIANT LABEL PLACEMENT: each label sits at the TRUE centroid of its state's
//    largest polygon ring, computed from the loaded GeoJSON (hardcoded coords are only a
//    fallback if no polygon data is available). The marker icon is a ZERO-SIZE point
//    anchored exactly at that latlng, and the text centers itself with CSS
//    transform:translate(-50%,-50%) — so centering does NOT depend on Leaflet's
//    iconAnchor negative margins (which a theme CSS reset like *{margin:0!important}
//    can wipe out, producing exactly the "off when zoomed out, fine when zoomed in"
//    constant-pixel drift). No fixed pixel offset can survive this construction.
// 6. CITY NAMES (our own, not Esri's): city labels come from a bundled theme asset
//    (assets/us-cities.csv — plotly's us-cities-top-1k dataset, keyless, same origin),
//    curated at runtime to the 300 most populous cities and rendered as small
//    non-interactive dot+name labels (10px, clearly smaller than the 12px state names).
//    They are zoom-TIERED: only ~9 spread-out majors at z4, ~36 at z5, ~79 at z6,
//    ~173 at z7, all 300 at z8+ — a greedy population-ranked pass demotes any city too
//    close to an already-visible bigger one at that tier, so satellite suburbs
//    (Mesa, Newark, Long Beach...) only appear when zoomed well in. This restores city
//    names WITHOUT the raster reference overlay, i.e. with no county lines and no
//    second set of state labels. If the city data fails to load, the map simply has
//    no city labels — nothing else is affected.
// 7. ROADS/HIGHWAYS: Esri's keyless World Transportation TRANSPARENT reference overlay
//    (roads + road/highway shields only — no basemap fill, no place/state/county
//    labels, so it cannot duplicate our labels or reintroduce county lines). Shown at
//    z5+ only (visual noise at national z3–4), toggled on zoomend like the labels.
//    It lives in a dedicated pane at z-index 450: ABOVE the satellite tiles (tilePane
//    200) and the state boundary vectors (overlayPane 400), BELOW our state/city label
//    markers and the dealer pins/clusters (markerPane 600) — so our labels and the red
//    pins always beat road shields for legibility. We deliberately do NOT add
//    World_Boundaries_and_Places (county lines + duplicate state labels), Hillshade
//    (redundant under real satellite), POI, or ocean-label layers.
//    DENSITY CONTROL: the layer uses tileSize:512 + zoomOffset:-1 (the standard Leaflet
//    pairing — the offset ONLY works with a matching tileSize, otherwise grid x/y
//    indices are computed in the wrong pyramid and tiles misplace/404). Every view
//    shows the road network from one zoom level lower: z5–7 is mainly interstates and
//    US highways, arterials arrive ~z8, residential streets fill in ~z9–10 instead of
//    immediately. Trade-off: road tiles are upscaled 2x (slightly softer lines/shields,
//    like non-retina tiles on a retina screen) — accepted for the density win.
//    (-2 with tileSize:1024 was considered and rejected: 4x upscale makes shield text
//    illegible and pushes residential streets out to ~z11-13.)
// 8. DEALER NAMES WITHOUT CLICKING: L.marker is monkeypatched so any marker created
//    with options.title (dealer-map.js builds every dealer pin with title: store name)
//    also gets a PERMANENT tooltip — a small dark pill above the pin. Clustered pins
//    aren't on the map, so names only appear once a pin declusters on zoom-in: exactly
//    the requested behavior, self-limited by the existing cluster radius. Our own
//    state/city label markers pass no title (unaffected); the geolocate dot is a
//    circleMarker (unaffected). Tooltips live in tooltipPane (z 650) — above roads and
//    our labels — and are pointer-events:none, so pin clicks and the existing
//    click-popups (bindPopup coexists with bindTooltip) keep working.
//    ZOOM GATE: name pills are additionally hidden until z>=12 (~7-9 mile radius on a
//    typical 800-1000px canvas at 40N: 29.3 m/px x width; z11 would be ~15-18 mi —
//    owner wants ~10-mile-radius views, erring close-in). A 'nyan-show-dealer-names'
//    class is toggled on the map container on zoomend, and the pill CSS defaults to
//    visibility:hidden (NOT display:none — Leaflet measures tooltip offsetWidth when
//    positioning; display:none measures 0 and the pill would appear half-width
//    off-center when revealed; visibility keeps layout size so positions stay exact).
//    A name therefore shows only when BOTH declustered AND z>=12.
(function () {
  if (typeof L === 'undefined' || !L.tileLayer || L.__nyanTileFix) return;
  L.__nyanTileFix = true;

  var _orig = L.tileLayer;
  var IMAGERY = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
  var ROADS = 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}';

  // Same-origin URL of the bundled outlines, derived from this script's own src
  // (e.g. /cdn/shop/t/NN/assets/dealer-map-tilefix.js?v=... -> .../assets/us-states-outline.json).
  var cs = document.currentScript;
  var LOCAL_STATES = (cs && cs.src) ? cs.src.replace(/dealer-map-tilefix\.js.*$/, 'us-states-outline.json') : null;
  var REMOTE_STATES = 'https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json';
  var LOCAL_CITIES = (cs && cs.src) ? cs.src.replace(/dealer-map-tilefix\.js.*$/, 'us-cities.csv') : null;
  var REMOTE_CITIES = 'https://raw.githubusercontent.com/plotly/datasets/master/us-cities-top-1k.csv';

  var STATES = [
    ['Alabama',32.8,-86.8],['Alaska',63.6,-152.5],['Arizona',34.3,-111.7],['Arkansas',34.8,-92.4],
    ['California',37.2,-119.6],['Colorado',39.0,-105.6],['Connecticut',41.6,-72.7],['Delaware',39.0,-75.5],
    ['Florida',28.4,-81.7],['Georgia',32.9,-83.4],['Hawaii',20.5,-156.5],['Idaho',44.2,-114.5],
    ['Illinois',40.0,-89.2],['Indiana',39.9,-86.3],['Iowa',42.0,-93.5],['Kansas',38.5,-98.3],
    ['Kentucky',37.6,-85.3],['Louisiana',31.0,-92.0],['Maine',45.4,-69.2],['Maryland',39.2,-76.8],
    ['Massachusetts',42.3,-71.9],['Michigan',44.3,-85.4],['Minnesota',46.3,-94.3],['Mississippi',32.7,-89.7],
    ['Missouri',38.4,-92.5],['Montana',46.9,-110.0],['Nebraska',41.5,-99.8],['Nevada',39.3,-116.6],
    ['New Hampshire',43.7,-71.6],['New Jersey',40.1,-74.7],['New Mexico',34.4,-106.1],['New York',42.9,-75.5],
    ['North Carolina',35.5,-79.4],['North Dakota',47.5,-100.5],['Ohio',40.3,-82.8],['Oklahoma',35.6,-97.5],
    ['Oregon',43.9,-120.6],['Pennsylvania',40.9,-77.8],['Rhode Island',41.7,-71.5],['South Carolina',33.9,-80.9],
    ['South Dakota',44.4,-100.2],['Tennessee',35.9,-86.4],['Texas',31.4,-99.4],['Utah',39.3,-111.7],
    ['Vermont',44.1,-72.7],['Virginia',37.6,-78.9],['Washington',47.4,-120.5],['West Virginia',38.7,-80.6],
    ['Wisconsin',44.6,-89.9],['Wyoming',43.0,-107.5]
  ];

  function ensureStyles() {
    if (document.getElementById('nyan-map-style')) return;
    var st = document.createElement('style');
    st.id = 'nyan-map-style';
    st.textContent =
      // Zero-size anchor div: kill every box property a theme reset or Leaflet default
      // could impose, and keep overflow visible so the self-centered text shows.
      '.nyan-state-label,.leaflet-div-icon.nyan-state-label{background:none !important;border:0 !important;box-shadow:none !important;margin:0 !important;padding:0 !important;width:0 !important;height:0 !important;overflow:visible !important;pointer-events:none !important;}' +
      // The text centers ITSELF on the anchor point via translate(-50%,-50%), measured
      // from its own rendered size — immune to margin/padding resets and to any
      // iconSize/iconAnchor mismatch. No fixed pixel offset is possible.
      '.nyan-state-label span{position:absolute;left:0;top:0;transform:translate(-50%,-50%);margin:0 !important;padding:0 !important;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;font-weight:700;font-size:12px;line-height:1;color:#fff;white-space:nowrap;letter-spacing:.01em;text-shadow:0 0 2px #000,0 0 3px #000,0 1px 2px rgba(0,0,0,.95);}' +
      // City labels: same zero-size/self-centering construction; visually SMALLER and
      // lighter than state names — 10px medium-weight text beside a 5px dot on the point.
      '.nyan-city-label,.leaflet-div-icon.nyan-city-label{background:none !important;border:0 !important;box-shadow:none !important;margin:0 !important;padding:0 !important;width:0 !important;height:0 !important;overflow:visible !important;pointer-events:none !important;}' +
      '.nyan-city-label i{position:absolute;left:0;top:0;transform:translate(-50%,-50%);width:5px;height:5px;border-radius:50%;background:#fff;box-shadow:0 0 0 1px rgba(0,0,0,.7);}' +
      '.nyan-city-label span{position:absolute;left:6px;top:0;transform:translateY(-50%);margin:0 !important;padding:0 !important;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;font-weight:600;font-size:10px;line-height:1;color:#fff;white-space:nowrap;letter-spacing:.01em;text-shadow:0 0 2px #000,0 0 3px #000,0 1px 2px rgba(0,0,0,.95);}' +
      // Dealer-name pill above each declustered pin (permanent tooltip). Overrides
      // Leaflet's white tooltip chrome; arrow hidden; never intercepts clicks.
      // Hidden by default; revealed only when the container carries the z>=12 class.
      // visibility (not display) so Leaflet's offsetWidth-based tooltip positioning
      // keeps measuring the real pill size while hidden.
      '.dealer-pin-label{visibility:hidden !important;background:rgba(12,12,14,.85) !important;color:#fff !important;border:1px solid rgba(255,255,255,.18) !important;border-radius:6px !important;padding:2px 7px !important;margin:0 !important;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;font-weight:600;font-size:11px;line-height:1.3;white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,.5) !important;pointer-events:none !important;}' +
      '.nyan-show-dealer-names .dealer-pin-label{visibility:visible !important;}' +
      '.dealer-pin-label::before{display:none !important;}';
    document.head.appendChild(st);
  }

  // --- true label positions from the loaded state polygons -------------------
  // Shoelace area + centroid of one ring, with longitude scaled by cos(lat) so
  // ring areas at different latitudes compare fairly. Returns [absArea, [lat,lng]].
  function ringAreaCentroid(ring, k) {
    var a = 0, cx = 0, cy = 0, n = ring.length, j = n - 1, i, x1, y1, x2, y2, cross;
    for (i = 0; i < n; j = i++) {
      x1 = ring[j][0] * k; y1 = ring[j][1];
      x2 = ring[i][0] * k; y2 = ring[i][1];
      cross = x1 * y2 - x2 * y1;
      a += cross; cx += (x1 + x2) * cross; cy += (y1 + y2) * cross;
    }
    a /= 2;
    if (!a) return null;
    return [Math.abs(a), [cy / (6 * a), (cx / (6 * a)) / k]];
  }

  // Label point for a feature = centroid of its LARGEST outer ring, so
  // MultiPolygon states label their mainland (Michigan -> Lower Peninsula,
  // Alaska -> mainland, Hawaii -> Big Island), never a stray island.
  function featureLabelPoint(geom) {
    if (!geom) return null;
    var rings = [];
    if (geom.type === 'Polygon') rings = [geom.coordinates[0]];
    else if (geom.type === 'MultiPolygon') geom.coordinates.forEach(function (p) { rings.push(p[0]); });
    else return null;
    var best = null, bestArea = 0;
    rings.forEach(function (r) {
      if (!r || r.length < 4) return;
      var k = Math.cos(r[0][1] * Math.PI / 180) || 1e-6;
      var res = ringAreaCentroid(r, k);
      if (res && res[0] > bestArea) { bestArea = res[0]; best = res[1]; }
    });
    return best;
  }

  // positions: {stateName: [lat,lng]} computed from real polygons, or null to
  // use the approximate hardcoded fallback coordinates.
  function addStateLabels(map, positions) {
    ensureStyles();
    var group = L.layerGroup();
    STATES.forEach(function (s) {
      var at = (positions && positions[s[0]]) || [s[1], s[2]];
      L.marker(at, {
        interactive: false,
        keyboard: false,
        zIndexOffset: -500, // keep labels under dealer pins/clusters
        icon: L.divIcon({ className: 'nyan-state-label', html: '<span>' + s[0] + '</span>', iconSize: [0, 0], iconAnchor: [0, 0] })
      }).addTo(group);
    });
    function sync() {
      var z = map.getZoom();
      var on = z >= 3 && z <= 6;
      if (on && !map.hasLayer(group)) group.addTo(map);
      else if (!on && map.hasLayer(group)) map.removeLayer(group);
    }
    map.on('zoomend', sync);
    sync();
  }

  function fetchJson(url) {
    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  // --- city labels -----------------------------------------------------------
  // Parse the bundled City,State,Population,lat,lon CSV (no quoted fields in this
  // dataset) and keep the 300 most populous cities.
  function parseCities(text) {
    var lines = text.split(/\r?\n/), out = [], i, p, pop, lat, lng;
    for (i = 1; i < lines.length; i++) {
      p = lines[i].split(',');
      if (p.length !== 5) continue;
      pop = +p[2]; lat = +p[3]; lng = +p[4];
      if (!isFinite(pop) || !isFinite(lat) || !isFinite(lng)) continue;
      out.push([p[0], lat, lng, pop]);
    }
    out.sort(function (a, b) { return b[3] - a[3]; });
    return out.slice(0, 300);
  }

  // Zoom tiering with collision thinning: walk cities in population order; each
  // starts at a rank-based tier (top 15 -> z4, top 50 -> z5, top 120 -> z6,
  // rest -> z7) and is demoted while it sits within that tier's minimum
  // separation (degrees, lng cos-scaled) of a bigger city already visible at
  // that tier. Everything shows from z8 up. Keeps zoomed-out views uncluttered
  // and stops suburb labels from piling onto their metro.
  var CITY_SEP = { 4: 3.0, 5: 1.5, 6: 0.7, 7: 0.35 };
  function tierCities(cities) {
    var placed = [];
    cities.forEach(function (c, i) {
      var t = i < 15 ? 4 : i < 50 ? 5 : i < 120 ? 6 : 7;
      while (t < 8) {
        var sep = CITY_SEP[t], clash = false, k = Math.cos(c[1] * Math.PI / 180);
        for (var j = 0; j < placed.length; j++) {
          var o = placed[j];
          if (o.t > t) continue;
          var dx = (o.c[2] - c[2]) * k, dy = o.c[1] - c[1];
          if (dx * dx + dy * dy < sep * sep) { clash = true; break; }
        }
        if (!clash) break;
        t++;
      }
      placed.push({ c: c, t: t });
    });
    return placed;
  }

  function addCityLabels(map) {
    if (!window.fetch) return;
    var attempts = LOCAL_CITIES ? [LOCAL_CITIES, REMOTE_CITIES] : [REMOTE_CITIES];
    (function tryNext(i) {
      if (i >= attempts.length) return; // no city labels — nothing else is affected
      fetch(attempts[i]).then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      }).then(function (text) {
        var cities = parseCities(text);
        if (!cities.length) throw new Error('no cities');
        ensureStyles();
        var groups = { 4: L.layerGroup(), 5: L.layerGroup(), 6: L.layerGroup(), 7: L.layerGroup(), 8: L.layerGroup() };
        tierCities(cities).forEach(function (pc) {
          L.marker([pc.c[1], pc.c[2]], {
            interactive: false,
            keyboard: false,
            zIndexOffset: -1000, // under state labels, well under dealer pins
            icon: L.divIcon({ className: 'nyan-city-label', html: '<i></i><span>' + pc.c[0] + '</span>', iconSize: [0, 0], iconAnchor: [0, 0] })
          }).addTo(groups[pc.t]);
        });
        function sync() {
          var z = map.getZoom();
          for (var t = 4; t <= 8; t++) {
            var on = z >= t;
            if (on && !map.hasLayer(groups[t])) groups[t].addTo(map);
            else if (!on && map.hasLayer(groups[t])) map.removeLayer(groups[t]);
          }
        }
        map.on('zoomend', sync);
        sync();
      }).catch(function () { tryNext(i + 1); });
    })(0);
  }

  // --- roads/highways overlay -------------------------------------------------
  // Transparent Esri World Transportation tiles in a dedicated pane between the
  // state-line vectors (overlayPane, z 400) and the label/pin markers
  // (markerPane, z 600). Visible from z5 up only.
  // tileSize:512 + zoomOffset:-1 fetches each view's roads from ONE ZOOM LOWER
  // (512px grid halves the tile indices so they line up with the z-1 pyramid —
  // never use zoomOffset without the matching tileSize). Result: only majors at
  // z5-7, residential streets from ~z9-10; tiles upscale 2x (slightly softer).
  // URL z spans 4..17 here (map z5..18, toggle hides below z5) — all exist.
  function addRoads(map) {
    var pane = map.getPane('nyan-roads') || map.createPane('nyan-roads');
    pane.style.zIndex = 450;
    pane.style.pointerEvents = 'none'; // never intercept clicks meant for pins/map
    var roads = _orig(ROADS, { pane: 'nyan-roads', tileSize: 512, zoomOffset: -1, maxNativeZoom: 18, maxZoom: 18 });
    function sync() {
      var on = map.getZoom() >= 5;
      if (on && !map.hasLayer(roads)) roads.addTo(map);
      else if (!on && map.hasLayer(roads)) map.removeLayer(roads);
    }
    map.on('zoomend', sync);
    sync();
  }

  function addBoundaries(map) {
    // Both sources are STATE-only polygons — county lines can never appear.
    // No raster boundary fallback (it would add duplicate labels + county lines);
    // worst case (all fetches fail) we still show our labels, just without lines.
    if (!window.fetch) { addStateLabels(map, null); return; }
    var attempts = LOCAL_STATES ? [LOCAL_STATES, REMOTE_STATES] : [REMOTE_STATES];
    (function tryNext(i) {
      if (i >= attempts.length) { addStateLabels(map, null); return; }
      fetchJson(attempts[i]).then(function (gj) {
        if (!gj || gj.type !== 'FeatureCollection' || !gj.features || !gj.features.length) throw new Error('bad geojson');
        L.geoJSON(gj, { interactive: false, style: { color: '#ffffff', weight: 1, opacity: 0.85, fill: false } }).addTo(map);
        // Real label anchors from the polygons themselves (name -> centroid latlng).
        var positions = {};
        gj.features.forEach(function (f) {
          var name = f && f.properties && f.properties.name;
          if (!name) return;
          var pt = featureLabelPoint(f.geometry);
          if (pt) positions[name] = pt;
        });
        addStateLabels(map, positions);
      }).catch(function () { tryNext(i + 1); });
    })(0);
  }

  function integerZoom(map) {
    map.options.zoomSnap = 1;
    map.options.zoomDelta = 1;
    var z = Math.round(map.getZoom());
    if (z !== map.getZoom()) { try { map.setZoom(z, { animate: false }); } catch (e) {} }
    var el = map.getContainer();
    var last = 0;
    el.addEventListener('wheel', function (e) {
      if (!e.ctrlKey) return; // plain scroll passes through (scrollWheelZoom is off by design)
      e.preventDefault();          // block browser page-zoom
      e.stopImmediatePropagation(); // suppress dealer-map.js's fractional handler
      var now = (window.performance && performance.now) ? performance.now() : new Date().getTime();
      if (now - last < 110) return; // rate-limit so pinch steps one integer level at a time
      last = now;
      var at = map.mouseEventToLatLng(e);
      map.setZoomAround(at, map.getZoom() + (e.deltaY < 0 ? 1 : -1));
    }, { passive: false, capture: true });
  }

  // Zoom gate for the dealer-name pills: toggle a class on the map container so
  // CSS shows the (permanent, but visibility:hidden) tooltips only from z12 up
  // (~7-9 mile radius viewport on a typical desktop canvas). Composes with
  // markercluster: a name renders only when its pin is BOTH declustered and z>=12.
  var DEALER_NAME_MIN_ZOOM = 12;
  function gateDealerNames(map) {
    var el = map.getContainer();
    function sync() {
      if (map.getZoom() >= DEALER_NAME_MIN_ZOOM) el.classList.add('nyan-show-dealer-names');
      else el.classList.remove('nyan-show-dealer-names');
    }
    map.on('zoomend', sync);
    sync(); // set the initial state on load too
  }

  // Dealer pins: dealer-map.js creates each pin as L.marker(ll, { icon:..., title: name }).
  // Bind that title as a permanent tooltip so the store name shows without a click once
  // the pin declusters (clustered markers aren't on the map, so no clutter when zoomed
  // out). Our own label markers pass no title; the geolocate dot is a circleMarker.
  var _marker = L.marker;
  L.marker = function (ll, opts) {
    var m = _marker.call(this, ll, opts);
    if (opts && opts.title && m.bindTooltip) {
      m.bindTooltip(opts.title, { permanent: true, direction: 'top', offset: [0, -34], className: 'dealer-pin-label', opacity: 1 });
    }
    return m;
  };

  L.tileLayer = function (url, opts) {
    if (typeof url === 'string' && url.indexOf('cartocdn.com') !== -1) {
      opts = opts || {};
      opts.attribution = 'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics';
      opts.maxNativeZoom = 18;
      opts.maxZoom = 18;
      delete opts.subdomains;
      var imagery = _orig.call(this, IMAGERY, opts);
      var _addTo = imagery.addTo;
      imagery.addTo = function (map) {
        integerZoom(map);
        var res = _addTo.call(this, map); // dealer-map.js listens for this layer's 'load' to hide its loader
        addBoundaries(map);
        addCityLabels(map);
        addRoads(map);
        gateDealerNames(map);
        return res;
      };
      return imagery;
    }
    return _orig.call(this, url, opts);
  };

  // Inject label/pill CSS up front so dealer-name tooltips are styled from the
  // very first decluster, independent of the async label-data fetches.
  ensureStyles();
})();
